import './CalendarDownloadButton.css';

/**
 * CalendarDownloadButton Component
 * 
 * Generates and downloads an ICS calendar file for an appointment.
 * 
 * @param {Object} props
 * @param {Date|string} props.date - The appointment date (Date object or ISO string)
 * @param {string} props.time - The appointment time (e.g., "10:00 AM")
 * @param {string} props.summary - Event title/summary (default: "Nurse Educator Appointment")
 * @param {string} props.description - Event description (default: includes phone number)
 * @param {string} props.location - Event location/phone (default: "1-833-462-7452")
 * @param {number} props.durationMinutes - Duration in minutes (default: 60)
 * @param {string} props.className - Additional CSS classes
 */
export default function CalendarDownloadButton({
  date,
  time,
  summary = 'Nurse Educator Appointment',
  description = 'Phone call appointment with a Nurse Educator',
  location = '1-833-462-7452',
  durationMinutes = 60,
  className = ''
}) {
  /**
   * Converts a time string (e.g., "10:00 AM") to 24-hour format
   */
  const parseTime = (timeStr) => {
    const [timePart, period] = timeStr.split(' ');
    const [hours, minutes] = timePart.split(':').map(Number);
    
    let hour24 = hours;
    if (period === 'PM' && hours !== 12) {
      hour24 = hours + 12;
    } else if (period === 'AM' && hours === 12) {
      hour24 = 0;
    }
    
    return { hours: hour24, minutes: minutes || 0 };
  };

  /**
   * Formats a Date object to ICS format (YYYYMMDDTHHMMSSZ)
   */
  const formatICSDate = (date) => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    
    return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
  };

  /**
   * Escapes special characters in ICS content
   */
  const escapeICS = (text) => {
    return text
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
  };

  /**
   * Generates the ICS file content and triggers download
   */
  const handleDownload = () => {
    if (!date || !time) {
      console.error('Date and time are required for calendar download');
      return;
    }

    // Parse the date - get year, month, day components
    const appointmentDate = date instanceof Date ? new Date(date) : new Date(date);
    const year = appointmentDate.getFullYear();
    const month = appointmentDate.getMonth();
    const day = appointmentDate.getDate();
    
    // Parse the time string (e.g., "10:00 AM")
    const { hours: estHours, minutes: estMinutes } = parseTime(time);
    
    // Create a date object representing the appointment time in EST
    // Note: We create it in UTC first, then adjust for EST offset
    // EST is UTC-5, so we need to add 5 hours to convert EST to UTC
    const utcDate = new Date(Date.UTC(year, month, day, estHours + 5, estMinutes, 0));
    
    // Calculate end time
    const endDate = new Date(utcDate);
    endDate.setUTCMinutes(endDate.getUTCMinutes() + durationMinutes);
    
    // Format dates for ICS
    const startICS = formatICSDate(utcDate);
    const endICS = formatICSDate(endDate);
    
    // Generate unique ID for the event
    const uid = `nurse-educator-${startICS}@inbrija.com`;
    
    // Get current timestamp for created/modified fields
    const now = formatICSDate(new Date());
    
    // Build ICS content
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//INBRIJA//Nurse Educator Scheduler//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      `DTSTART:${startICS}`,
      `DTEND:${endICS}`,
      `SUMMARY:${escapeICS(summary)}`,
      `DESCRIPTION:${escapeICS(description)}\\n\\nPhone: ${location}`,
      `LOCATION:${escapeICS(location)}`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    
    // Create blob and trigger download
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `nurse-educator-appointment-${startICS}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <button
      type="button"
      className={`calendar-download-btn ${className}`}
      onClick={handleDownload}
    >
      Add to your calendar
    </button>
  );
}

